import { Observer, observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotification } from '@/Ship/Hooks/useNotification'
import { Form, FormField } from '@/Core/UI/Form'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { PasswordRules, type PasswordRule } from '@/Ship/Components/Form/PasswordRules'
import { FormActionsBar } from '@/Ship/Components/UI/FormActionsBar'
import { cx } from '@/Ship/Helpers/cssClass'

import { PASSWORD_MIN_LENGTH, checkPassword } from '@/Ship/Helpers/passwordRules'
import { ChangePasswordStore } from '../../../Stores/ChangePasswordStore'

import shell from '@/Ship/Components/UI/SettingsShell/SettingsShell.module.css'

/**
 * Розділ «Змінити пароль»: два поля, вимоги під ними з позначками виконання,
 * кнопка активна лише коли пароль достатньо довгий і підтвердження збігається.
 * Поточний пароль бекенд не вимагає.
 */
export const ChangePassword = observer(() => {
    const { t } = useTranslation()
    const { showSuccess, showError } = useNotification()
    const [store] = useState(() => new ChangePasswordStore())

    const tp = (key: string, options?: Record<string, unknown>) => t(key, { ns: 'Setting.Profile', ...options })

    const [formContext] = useState(() =>
        createFormContext({
            fields: {
                password: {
                    type: 'password',
                    label: tp('New password'),
                    isRequired: true,
                    inputProps: { placeholder: tp('New password'), autoComplete: 'new-password' },
                },
                password_confirmation: {
                    type: 'password',
                    label: tp('Confirm new password'),
                    isRequired: true,
                    inputProps: { placeholder: tp('Confirm new password'), autoComplete: 'new-password' },
                },
            },
            initialValues: { password: '', password_confirmation: '' },
            onSubmit: async ({ values }: any) => {
                const ok = await store.submit(values.password, {
                    success: () => showSuccess(tp('Update information'), tp('Password successfully updated.')),
                    error: (msg: string) => showError(tp('Error'), msg),
                })
                if (ok) formContext.reset()
            },
        })
    )

    // Значення читаємо лише в <Observer> всередині форми, не тут: Form ядра на кожен свій
    // перерендер перемонтовує вміст (UIManager.render створює новий observer-компонент),
    // і перерендер цього компонента на кожну літеру скидав би фокус поля
    const getCheck = () =>
        checkPassword(
            String(formContext.values['password'] ?? ''),
            String(formContext.values['password_confirmation'] ?? '')
        )

    const buildRules = (check: ReturnType<typeof checkPassword>): PasswordRule[] => [
        {
            key: 'length',
            done: check.lengthOk,
            label: tp('At least {{count}} characters', { count: PASSWORD_MIN_LENGTH }),
        },
        {
            key: 'match',
            done: check.matchOk,
            failed: check.mismatch,
            label: tp('Passwords match'),
            failedLabel: tp('Passwords do not match'),
        },
    ]

    return (
        <Form
            formContext={formContext}
            onSubmit={() => {
                if (getCheck().canSubmit) void formContext.submit()
            }}
            isLanguageSwitcher={false}
        >
            <div className={cx(shell, 'stack')}>
                <div className={cx(shell, 'section')}>
                    <div className={cx(shell, 'sectionHead')}>
                        <h2 className={cx(shell, 'sectionTitle')}>{tp('Change password')}</h2>
                        <p className={cx(shell, 'sectionHint')}>{tp('Enter the new password twice.')}</p>
                    </div>
                    <div className={cx(shell, 'grid2')}>
                        <FormField name="password" />
                        <FormField name="password_confirmation" />
                    </div>
                    <Observer>{() => <PasswordRules rules={buildRules(getCheck())} />}</Observer>
                </div>

                <div className={cx(shell, 'actions')}>
                    <Observer>
                        {() => (
                            <FormActionsBar
                                dirty={getCheck().canSubmit}
                                saving={store.loading}
                                onReset={() => formContext.reset()}
                                submitLabel={tp('Change password')}
                                resetLabel={tp('Cancel')}
                                statusText={null}
                            />
                        )}
                    </Observer>
                </div>
            </div>
        </Form>
    )
})
